import { useQuery } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';
import { api } from '../client';
import { ApiFailure } from '@/lib/result';

type MeResult = Awaited<ReturnType<typeof api.auth.me>>;

/** The signed-in person: enough to label the header and gate screens by role. */
export type Me = Extract<MeResult, { status: 200 }>['body']['user'];

export const ME_KEY = ['auth', 'me'] as const;

/** Is this cache key the `me` query's? Kept when everything else is dropped. */
export const isMeKey = (key: readonly unknown[]): boolean =>
  key.length === ME_KEY.length && key.every((part, i) => part === ME_KEY[i]);

/**
 * Who is signed in: the user, or `null` when the API says 401. The app shows
 * the sign-in overlay for `null`; any later 401 re-runs this query
 * (client.ts), which is how an expired session brings the overlay back.
 */
export function useMe(): UseQueryResult<Me | null> {
  return useQuery({
    queryKey: ME_KEY,
    queryFn: async () => {
      const result = await api.auth.me();
      if (result.status === 200) return result.body.user;
      if (result.status === 401) return null;
      throw new ApiFailure(result.status, undefined);
    },
    retry: false,
    staleTime: 60_000,
  });
}
